import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button, Alert } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import liveStreamService, { ViewerCredentials, LiveStreamError } from '../services/live-stream-service';
import LiveStreamViewer from '../components/LiveStream/LiveStreamViewer';

const WatchStreamPage: React.FC = () => {
  const { streamId } = useParams<{ streamId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<ViewerCredentials | null>(null);
  const [streamTitle, setStreamTitle] = useState('');
  const streamInfoRef = useRef({ title: '', broadcasterName: '', viewerCount: 0, duration: 0 });

  // Hide header/footer/nav when watching stream
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.classList.add('broadcasting-mode');
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('broadcasting-mode');
    };
  }, []);

  useEffect(() => {
    if (!streamId) {
      setError('No stream ID provided');
      setLoading(false);
      return;
    }
    joinStream();
  }, [streamId]);

  const joinStream = async () => {
    if (!streamId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await liveStreamService.joinStream(streamId);
      setCredentials(result);
      try {
        const stream = await liveStreamService.getStream(streamId);
        setStreamTitle(stream.title);
        streamInfoRef.current = { title: stream.title, broadcasterName: '', viewerCount: 0, duration: 0 };
      } catch {
        setStreamTitle('Live Stream');
        streamInfoRef.current = { title: 'Live Stream', broadcasterName: '', viewerCount: 0, duration: 0 };
      }
    } catch (err) {
      const message = err instanceof LiveStreamError ? err.message : 'Failed to join stream';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Stable callbacks that don't change reference
  const handleLeave = useCallback(async () => {
    if (streamId) {
      try {
        await liveStreamService.leaveStream(streamId);
      } catch (err) {
        console.error('Error leaving stream:', err);
      }
    }
    navigate('/live');
  }, [streamId, navigate]);

  const handleError = useCallback((err: string) => {
    console.error('Stream error:', err);
  }, []);

  const handleStreamEnd = useCallback(async () => {
    if (streamId) {
      try {
        await liveStreamService.leaveStream(streamId);
      } catch { /* ignore */ }
    }
    navigate('/live');
  }, [streamId, navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography>Joining stream...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>{error}</Alert>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={() => navigate('/live')}>
          Back to Live Streams
        </Button>
      </Box>
    );
  }

  if (!credentials || !streamId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Unable to load stream</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/live')}>
          Back to Live Streams
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: '#000' }}>
      <LiveStreamViewer
        streamId={streamId}
        playbackUrl={credentials.playbackUrl}
        chatToken={credentials.chatToken}
        streamInfo={streamInfoRef.current}
        onLeave={handleLeave}
        onError={handleError}
        onStreamEnd={handleStreamEnd}
      />
    </Box>
  );
};

export default WatchStreamPage;
